import type { Booking } from "@/lib/bookings";
import type { Farm, Farmer, FarmerSettlement } from "@/lib/adminModules";
import { formatRupees } from "@/pages/admin/format";

export type Tone = "primary" | "accent" | "secondary";

export interface KpiCard {
  label: string;
  value: string;
  delta: number;
  icon: string;
  tone: Tone;
  live: boolean;
}

export interface RevenuePoint {
  month: string;
  gross: number;
  commission: number;
  farmer: number;
  refunds: number;
}

export interface VisitorSegment {
  label: string;
  value: number;
  color: string;
}

export interface FarmPerformanceRow {
  farm: string;
  bookings: number;
  visitors: number;
  revenue: number;
  rating: number;
  utilization: number;
  status: "Active" | "Limited" | "Paused";
}

export interface PendingAction {
  label: string;
  count: number;
  icon: string;
  tone: Tone;
  to: string;
}

export interface ActivityEntry {
  title: string;
  sub: string;
  time: string;
  icon: string;
  tone: Tone;
}

export const revenueByMonth: RevenuePoint[] = [
  { month: "Nov", gross: 420000, commission: 84000, farmer: 336000, refunds: 12000 },
  { month: "Dec", gross: 512000, commission: 102400, farmer: 409600, refunds: 18500 },
  { month: "Jan", gross: 468000, commission: 93600, farmer: 374400, refunds: 9400 },
  { month: "Feb", gross: 505000, commission: 101000, farmer: 404000, refunds: 15200 },
  { month: "Mar", gross: 540000, commission: 108000, farmer: 432000, refunds: 11000 },
  { month: "Apr", gross: 498000, commission: 99600, farmer: 398400, refunds: 8800 },
  { month: "May", gross: 560000, commission: 112000, farmer: 448000, refunds: 16400 },
  { month: "Jun", gross: 610000, commission: 122000, farmer: 488000, refunds: 21300 },
  { month: "Jul", gross: 585000, commission: 117000, farmer: 468000, refunds: 13700 },
  { month: "Aug", gross: 642000, commission: 128400, farmer: 513600, refunds: 17600 },
  { month: "Sep", gross: 598000, commission: 119600, farmer: 478400, refunds: 12900 },
  { month: "Oct", gross: 668000, commission: 133600, farmer: 534400, refunds: 19500 },
];

export const visitorSegments: VisitorSegment[] = [
  { label: "Individual", value: 1920, color: "oklch(var(--primary-500))" },
  { label: "Families", value: 1450, color: "oklch(var(--accent-500))" },
  { label: "Schools", value: 620, color: "oklch(var(--secondary-500))" },
  { label: "Corporate", value: 310, color: "oklch(var(--primary-300))" },
  { label: "Tourists", value: 180, color: "oklch(var(--foreground-400))" },
];

export const farmPerformance: FarmPerformanceRow[] = [
  { farm: "Sri Lakshmi Farm", bookings: 42, visitors: 118, revenue: 142500, rating: 4.8, utilization: 82, status: "Active" },
  { farm: "Green Valley Farm", bookings: 36, visitors: 94, revenue: 112800, rating: 4.7, utilization: 76, status: "Active" },
  { farm: "Konaseema Coconut Farm", bookings: 29, visitors: 81, revenue: 89500, rating: 4.9, utilization: 69, status: "Active" },
  { farm: "Godavari Riverside Farm", bookings: 24, visitors: 67, revenue: 74200, rating: 4.6, utilization: 64, status: "Limited" },
  { farm: "Sunrise Paddy Fields", bookings: 18, visitors: 52, revenue: 58900, rating: 4.5, utilization: 71, status: "Active" },
  { farm: "Rythu Organic Farm", bookings: 15, visitors: 41, revenue: 46300, rating: 4.4, utilization: 58, status: "Limited" },
];

export const pendingActions: PendingAction[] = [
  { label: "Farmer Applications", count: 8, icon: "ri-file-user-line", tone: "accent", to: "/admin/farmer-applications" },
  { label: "Booking Requests", count: 5, icon: "ri-calendar-check-line", tone: "primary", to: "/admin/bookings" },
  { label: "Farms Awaiting Verification", count: 3, icon: "ri-shield-check-line", tone: "secondary", to: "/admin/verification" },
  { label: "Safety Audits Due", count: 4, icon: "ri-shield-flash-line", tone: "accent", to: "/admin/safety-audits" },
  { label: "Refund Requests", count: 6, icon: "ri-refund-2-line", tone: "primary", to: "/admin/refunds" },
  { label: "Support Tickets", count: 7, icon: "ri-customer-service-2-line", tone: "secondary", to: "/admin/support-tickets" },
];

export const activityFeed: ActivityEntry[] = [
  { title: "Admin approved farmer", sub: "Ramesh Naidu · Sri Lakshmi Farm", time: "12 min ago", icon: "ri-user-follow-line", tone: "primary" },
  { title: "Booking #RTG-10482 confirmed", sub: "Anil Kumar · Farmer for a Day", time: "48 min ago", icon: "ri-calendar-check-line", tone: "accent" },
  { title: "Farm price updated", sub: "Konaseema Coconut Farm · ₹899 → ₹999", time: "2 hours ago", icon: "ri-price-tag-3-line", tone: "secondary" },
  { title: "New review received", sub: "5★ · Green Valley Farm", time: "3 hours ago", icon: "ri-star-line", tone: "accent" },
  { title: "Settlement processed", sub: "₹42,500 → Sri Lakshmi Farm", time: "Yesterday", icon: "ri-hand-coin-line", tone: "primary" },
  { title: "Content published", sub: "Monsoon in Konaseema · Blog", time: "Yesterday", icon: "ri-article-line", tone: "secondary" },
];

function todayISO(): string {
  const now = new Date();
  return `${now.getFullYear()}-${`${now.getMonth() + 1}`.padStart(2, "0")}-${`${now.getDate()}`.padStart(2, "0")}`;
}

export interface DashboardLive {
  bookings: Booking[];
  activeFarms: number;
  activeExperiences: number;
  pendingApprovals: number;
  pendingSettlements: number;
  todayRevenue: number;
}

export function buildKpis(data: DashboardLive): KpiCard[] {
  const today = todayISO();
  const upcomingVisitors = data.bookings
    .filter((b) => (b.preferred_date || "") >= today && b.status !== "cancelled")
    .reduce((sum, b) => sum + (b.group_size || 0), 0);

  return [
    { label: "Total Bookings", value: new Intl.NumberFormat("en-IN").format(data.bookings.length), delta: 12.4, icon: "ri-calendar-check-line", tone: "primary", live: true },
    { label: "Upcoming Visitors", value: new Intl.NumberFormat("en-IN").format(upcomingVisitors), delta: 8.1, icon: "ri-group-line", tone: "secondary", live: true },
    { label: "Today's Revenue", value: formatRupees(data.todayRevenue), delta: 5.6, icon: "ri-money-rupee-circle-line", tone: "accent", live: true },
    { label: "Active Farms", value: new Intl.NumberFormat("en-IN").format(data.activeFarms), delta: 2.3, icon: "ri-plant-line", tone: "primary", live: true },
    { label: "Active Experiences", value: new Intl.NumberFormat("en-IN").format(data.activeExperiences), delta: 6.7, icon: "ri-compass-3-line", tone: "secondary", live: true },
    { label: "Pending Approvals", value: new Intl.NumberFormat("en-IN").format(data.pendingApprovals), delta: -4.2, icon: "ri-error-warning-line", tone: "accent", live: true },
    { label: "Pending Settlements", value: formatRupees(data.pendingSettlements), delta: 3.9, icon: "ri-hand-coin-line", tone: "primary", live: true },
    { label: "Open Issues", value: "7", delta: -11.5, icon: "ri-alarm-warning-line", tone: "accent", live: false },
  ];
}

const FARM_STATUS_LABEL: Record<string, FarmPerformanceRow["status"]> = {
  active: "Active",
  verified: "Active",
  pending_approval: "Limited",
  draft: "Limited",
  temporarily_closed: "Limited",
  suspended: "Paused",
  archived: "Paused",
};

/** Builds the farm-performance table from the live Farms, Farmers & Settlements tables. */
export function buildFarmPerformance(
  farms: Farm[],
  farmers: Farmer[],
  settlements: FarmerSettlement[],
): FarmPerformanceRow[] {
  const rows = farms.map((farm) => {
    const revenue = settlements
      .filter((s) => s.farm_name === farm.name)
      .reduce((sum, s) => sum + Number(s.final_settlement || 0), 0);
    const farmer = farmers.find((f) => f.name === farm.farmer_name);
    const bookings = farmer?.total_bookings ?? 0;
    const visitors = Math.round(bookings * 2.6);
    return {
      farm: farm.name,
      bookings,
      visitors,
      revenue: revenue || Math.round(Number(farmer?.total_revenue || 0)),
      rating: Number(farm.rating || 0),
      utilization: 0,
      status: FARM_STATUS_LABEL[farm.status] || "Active",
    };
  });

  const maxBookings = Math.max(1, ...rows.map((r) => r.bookings));
  const ranked = rows
    .map((r) => ({ ...r, utilization: Math.min(98, Math.round((r.bookings / maxBookings) * 100)) }))
    .sort((a, b) => b.revenue - a.revenue || b.bookings - a.bookings)
    .slice(0, 6);

  return ranked;
}

export function sumRevenue(points: RevenuePoint[]) {
  return points.reduce(
    (acc, point) => ({
      gross: acc.gross + point.gross,
      commission: acc.commission + point.commission,
      farmer: acc.farmer + point.farmer,
      refunds: acc.refunds + point.refunds,
    }),
    { gross: 0, commission: 0, farmer: 0, refunds: 0 },
  );
}